import { useRef, useEffect } from "react";
import { gsap } from "../../lib/gsap";
import Container from "../layout/Container";
import Section from "../layout/Section";

import cloudImg from "../../assets/images/trust/cloud.jpeg";
import securityImg from "../../assets/images/trust/security.jpg";
import networkImg from "../../assets/images/trust/network.jpg";

/* ============================= */
/* Animated Block Wrapper */
/* ============================= */
function AnimatedBlock({ children }) {
  const ref = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      ref.current,
      { opacity: 0, y: 80 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 85%",
        },
      }
    );
  }, []);

  return <div ref={ref}>{children}</div>;
}

export default function Trust() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        sectionRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 90%",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section
      id="infrastructure"
      className="relative bg-slate-950 text-white py-32 overflow-hidden"
    >
      {/* Subtle depth background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pointer-events-none" />

      <Container>
        <div ref={sectionRef} className="relative space-y-32">

          {/* Header */}
          <AnimatedBlock>
            <div className="max-w-4xl">
              <h2 className="text-4xl md:text-6xl font-semibold leading-tight mb-8">
                Infrastructure Built for Autonomous Scale
              </h2>

              <p className="text-slate-400 text-lg leading-relaxed">
                A globally distributed infrastructure stack designed
                for secure data ingestion, fleet-scale model training,
                synchronized deployments, and mission-critical uptime.
              </p>
            </div>
          </AnimatedBlock>

          {/* Cloud Block */}
          <AnimatedBlock>
            <div className="grid md:grid-cols-2 gap-20 items-center">

              <div className="group relative rounded-3xl overflow-hidden border border-slate-800 transition-all duration-500 hover:shadow-2xl hover:shadow-indigo-500/10">
                <img
                  src={cloudImg}
                  alt="Cloud infrastructure"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-indigo-500/10 to-transparent transition-opacity duration-500 pointer-events-none" />
              </div>

              <div className="space-y-6">
                <h3 className="text-2xl font-semibold">
                  Distributed Cloud Architecture
                </h3>

                <p className="text-slate-400 leading-relaxed">
                  Secure ingestion pipelines aggregate fleet telemetry
                  in real time, enabling continuous retraining and
                  high-fidelity operational insight.
                </p>

                <ul className="space-y-3 text-slate-500">
                  <li>• Fleet-wide telemetry streaming</li>
                  <li>• Scalable AI retraining clusters</li>
                  <li>• Edge-to-cloud synchronization</li>
                </ul>
              </div>
            </div>
          </AnimatedBlock>

          {/* Security Block */}
          <AnimatedBlock>
            <div className="grid md:grid-cols-2 gap-20 items-center">

              <div className="space-y-6">
                <h3 className="text-2xl font-semibold">
                  Enterprise-Grade Security
                </h3>

                <p className="text-slate-400 leading-relaxed">
                  End-to-end encryption, firmware validation,
                  and deterministic execution layers ensure
                  operational integrity across all deployments.
                </p>

                <ul className="space-y-3 text-slate-500">
                  <li>• Encrypted data channels</li>
                  <li>• Secure OTA update validation</li>
                  <li>• Compliance-ready infrastructure</li>
                </ul>
              </div>

              <div className="group relative rounded-3xl overflow-hidden border border-slate-800 transition-all duration-500 hover:shadow-2xl hover:shadow-indigo-500/10">
                <img
                  src={securityImg}
                  alt="Security systems"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-indigo-500/10 to-transparent transition-opacity duration-500 pointer-events-none" />
              </div>
            </div>
          </AnimatedBlock>

          {/* Network Block */}
          <AnimatedBlock>
            <div className="grid md:grid-cols-2 gap-20 items-center">

              <div className="group relative rounded-3xl overflow-hidden border border-slate-800 transition-all duration-500 hover:shadow-2xl hover:shadow-indigo-500/10">
                <img
                  src={networkImg}
                  alt="Global network"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-indigo-500/10 to-transparent transition-opacity duration-500 pointer-events-none" />
              </div>

              <div className="space-y-6">
                <h3 className="text-2xl font-semibold">
                  Global Fleet Synchronization
                </h3>

                <p className="text-slate-400 leading-relaxed">
                  Centralized orchestration guarantees model consistency,
                  coordinated deployments, and real-time visibility across
                  distributed mobility networks.
                </p>

                <ul className="space-y-3 text-slate-500">
                  <li>• Model consistency enforcement</li>
                  <li>• Real-time deployment monitoring</li>
                  <li>• Cross-region resilience architecture</li>
                </ul>
              </div>
            </div>
          </AnimatedBlock>

        </div>
      </Container>
    </Section>
  );
}